import { Injectable } from "@angular/core";
import { forkJoin, Observable, of } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { persistAuthSession } from "../utils/auth-session.util";
import {
  mapPdfDownloadHttpError,
  mapPdfDownloadHttpResponse,
} from "../utils/booking-pdf-download.util";
import { HttpService } from "./http.service";
import { WorkspaceContextService } from "./workspace-context.service";
import { FileViewService } from "./file-view.service";
import { API_CONFIG } from "../config/api.config";
import {
  ApiEnvelope,
  ApiRecord,
  formatInr,
  formatPortalStatus,
  rec,
  unwrapApiData,
} from "../models/api-response.model";
import {
  extractAadhaarDisplayNumber,
  formatToIsoDate,
  normalizeGenderForApi,
  resolveEnquirySourceDetails,
  resolveParkingNo,
  type BookingDocumentUploadPayload,
  type PartyType,
} from "../pages/bookings/booking-details/booking-details.mapper";
import { toNativeBlob } from '../utils/http-blob.util';
import {
  mapOwnersFromBookingApi,
  pickContactString,
  resolvePartiesFromBookingApi,
} from "../pages/property-registrations/property-registration.mapper";
import type {
  ConfirmGatewayPaymentRequest,
  ConfirmGatewayPaymentResponse,
  InitiateGatewayPaymentRequest,
  InitiateGatewayPaymentResponse,
} from "../models/payment-gateway.model";
import { PaymentGatewayService } from "./payment-gateway.service";
import { NamePrefixOptionsService } from "./name-prefix-options.service";
import { resolvePartyPersonDisplayName } from "../pages/bookings/utils/name-prefix.util";

export interface BookingListQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  isRegistrationEligible?: boolean;
  /** Defaults to session JWT workspace when omitted. */
  workspaceId?: string;
  /** When true, do not fall back to session workspace (multi-builder picker). */
  requireWorkspaceId?: boolean;
}

export interface BookingPropertyCard {
  id: string;
  workspaceId?: string;
  workspaceName?: string;
  name: string;
  badge: string;
  projectBy: string;
  location: string;
  unitNo: string;
  tower: string;
  propertyType: string;
  parkingType: string;
  area: string;
  amount: string;
  bookingAmount: string;
  gstAmount: string;
  registrationAmount?: string;
  stampDuty?: string;
  imageUrl: string;
  projectImageKey: string;
  status?: string;
}

interface BookingListApiRow {
  id: string;
  workspaceId?: string;
  workspaceName?: string;
  bookingId?: string;
  projectName?: string;
  developerName?: string | null;
  location?: string | null;
  projectImageUrl?: string | null;
  status?: string;
  propertyAmount?: number | null;
  bookingAmount?: number | null;
  gstAmount?: number | null;
  unit?: {
    tower?: string | null;
    unitNumber?: string | null;
    wing?: string | null;
    bhk?: string | null;
    carpetAreaSqFt?: number | null;
    parking?: string | null;
  };
  stampDutyAmount?: number | null;
  registrationFeeAmount?: number | null;
}

interface StandardBookingListItem {
  id: string;
  bookingNumber?: string;
  projectName?: string | null;
  developerName?: string | null;
  location?: string | null;
  towerName?: string | null;
  unitNumber?: string;
  unitType?: string | null;
  carParking?: string | null;
  carpetAreaSqFt?: number | null;
  totalValue?: number;
  bookingAmount?: number;
  gstAmount?: number | null;
  projectAddress?: string | null;
  projectImageUrl?: string | null;
  status?: string;
  stampDutyAmount?: number | null;
  registrationFeeAmount?: number | null;
  owner?: {
    email?: string | null;
    name?: string | null;
    mobile?: string | null;
  };
}

@Injectable({ providedIn: "root" })
export class BookingService {
  constructor(
    private readonly http: HttpService,
    private readonly paymentGateway: PaymentGatewayService,
    private readonly fileViewService: FileViewService,
    private readonly namePrefixOptionsService: NamePrefixOptionsService,
    private readonly workspaceContext: WorkspaceContextService,
  ) {
    this.namePrefixOptionsService.loadActiveOptions().subscribe();
  }

  /** Sets workspace context before `/v1/bookings` operational APIs. */
  prepareBookingWorkspaceContext(workspaceId?: string | null): void {
    this.workspaceContext.setActiveWorkspaceId(workspaceId);
  }


  listAssociatedBuilders(): Observable<
    { workspaceId: string; workspaceName: string }[]
  > {
    return this.http
      .get<
        ApiEnvelope<{
          builders?: { workspaceId: string; workspaceName: string }[];
          workspaces?: { workspaceId: string; workspaceName: string }[];
        }>
      >(API_CONFIG.auth.workspaces)
      .pipe(
        map((res) => {
          const data = unwrapApiData(res) as
            | {
                builders?: { workspaceId: string; workspaceName: string }[];
                workspaces?: { workspaceId: string; workspaceName: string }[];
              }
            | undefined;
          if (Array.isArray(data?.workspaces) && data.workspaces.length) {
            return data.workspaces;
          }
          return Array.isArray(data?.builders) ? data.builders : [];
        }),
      );
  }

  switchBuilderWorkspace(workspaceId: string): Observable<void> {
    const ws = workspaceId?.trim();
    if (!ws) {
      return of(undefined);
    }
    const refreshToken =
      typeof localStorage === "undefined"
        ? undefined
        : localStorage.getItem("refreshToken") ?? undefined;
    return this.http
      .post<
        ApiEnvelope<{
          accessToken?: string;
          refreshToken?: string;
        }>
      >(API_CONFIG.auth.switchWorkspace, {
        workspaceId: ws,
        refreshToken,
        setAsDefault: true,
      })
      .pipe(
        tap((res) => {
          const data = unwrapApiData(res) as
            | { accessToken?: string; refreshToken?: string }
            | undefined;
          if (data?.accessToken?.trim()) {
            persistAuthSession({
              accessToken: data.accessToken.trim(),
              refreshToken: data.refreshToken?.trim() || refreshToken,
              workspaceId: ws,
            });
          }
          this.prepareBookingWorkspaceContext(ws);
        }),
        map(() => undefined),
      );
  }

  /** Property Bookings submodule list — requires active workspace from sidebar picker. */
  listPropertyBookings(
    params?: BookingListQueryParams,
  ): Observable<BookingPropertyCard[]> {
    const ws = this.workspaceContext.getActiveWorkspaceId()?.trim();
    return this.listBookings({
      ...params,
      workspaceId: ws || params?.workspaceId,
      requireWorkspaceId: true,
    });
  }

  listBookings(params?: BookingListQueryParams): Observable<BookingPropertyCard[]> {
    const explicitWorkspace = params?.workspaceId?.trim();
    const sessionWorkspace = params?.requireWorkspaceId
      ? ''
      : this.workspaceContext.getActiveWorkspaceId()?.trim() ?? '';
    const workspaceId = explicitWorkspace || sessionWorkspace;
    const query: BookingListQueryParams = { ...params };
    if (workspaceId) {
      query.workspaceId = workspaceId;
      this.prepareBookingWorkspaceContext(workspaceId);
    }
    return this.http
      .get<
        ApiEnvelope<{
          data?: StandardBookingListItem[] | { data?: StandardBookingListItem[] };
          bookings?: BookingListApiRow[];
          pagination?: unknown;
        }>
      >(
        API_CONFIG.customerBookings.list,
        query as Record<string, string | number | boolean>,
      )
      .pipe(
        switchMap((res) => {
          const rows = this.extractBookingListRows(unwrapApiData(res));
          const apiRows = rows.map((row) => this.mapListItemToApiRow(row));
          return this.resolveBookingCardImages(apiRows);
        }),
      );
  }

  listRegistrationBookings(
    params?: Pick<BookingListQueryParams, "page" | "limit" | "search" | "status">,
  ): Observable<BookingPropertyCard[]> {
    return this.listPropertyBookings({
      ...params,
      isRegistrationEligible: true,
    });
  }

  getOverview(
    bookingId: string,
    workspaceId?: string,
  ): Observable<Record<string, unknown>> {
    this.prepareBookingWorkspaceContext(workspaceId);
    return this.getBookingDetails(bookingId).pipe(
      map((detail) => this.overviewFromBookingDetail(detail)),
    );
  }

  overviewFromBookingDetail(
    detail: Record<string, unknown>,
  ): Record<string, unknown> {
    return this.mapBookingDetailToOverview(rec(detail));
  }

  ownerSummaryFromBookingDetail(
    detail: Record<string, unknown>,
  ): Record<string, unknown> {
    return this.mapBookingDetailToOwnerSummary(rec(detail));
  }

  getBookingDashboard(bookingId: string): Observable<Record<string, unknown>> {
    return this.http
      .get<ApiEnvelope<Record<string, unknown>>>(
        API_CONFIG.bookings.dashboard(bookingId),
      )
      .pipe(map((res) => unwrapApiData(res) ?? {}));
  }

  paymentSummaryFromDashboardAndDetail(
    dashboard: Record<string, unknown>,
    detail: Record<string, unknown>,
  ): Record<string, unknown> {
    return this.mapDashboardToPaymentSummary(rec(dashboard), rec(detail));
  }

  getTimeline(bookingId: string): Observable<unknown> {
    return this.http
      .get<ApiEnvelope<unknown>>(API_CONFIG.bookings.timeline(bookingId))
      .pipe(map((res) => unwrapApiData(res)));
  }

  getOwnerWorkflow(
    bookingId: string,
    options?: { partyType?: PartyType; customerId?: string },
  ): Observable<Record<string, unknown>> {
    const params: Record<string, string> = {};
    if (options?.partyType) {
      params["partyType"] = options.partyType;
    }
    if (options?.customerId?.trim()) {
      params["customerId"] = options.customerId.trim();
    }
    return this.http
      .get<
        ApiEnvelope<Record<string, unknown>>
      >(API_CONFIG.bookings.ownerWorkflow(bookingId), params)
      .pipe(map((res) => unwrapApiData(res) ?? {}));
  }

  getPaymentSummary(
    bookingId: string,
    bookingDetail?: Record<string, unknown>,
  ): Observable<Record<string, unknown>> {
    return this.getBookingDashboard(bookingId).pipe(
      switchMap((dashboard) => {
        if (bookingDetail) {
          return of(
            this.paymentSummaryFromDashboardAndDetail(dashboard, bookingDetail),
          );
        }
        return this.getBookingDetails(bookingId).pipe(
          map((detail) =>
            this.paymentSummaryFromDashboardAndDetail(dashboard, detail),
          ),
        );
      }),
    );
  }

  updateOverview(
    bookingId: string,
    body: { financeType: string },
  ): Observable<unknown> {
    return this.http
      .patch<
        ApiEnvelope<unknown>
      >(API_CONFIG.bookings.updateOverview(bookingId), body)
      .pipe(map((res) => unwrapApiData(res)));
  }

  getOwnerSummary(bookingId: string): Observable<Record<string, unknown>> {
    return this.getBookingDetails(bookingId).pipe(
      map((detail) => this.ownerSummaryFromBookingDetail(detail)),
    );
  }

  getBookingDetails(bookingId: string): Observable<Record<string, unknown>> {
    return this.http
      .get<ApiEnvelope<Record<string, unknown>> | Record<string, unknown>>(
        API_CONFIG.bookings.get(bookingId),
      )
      .pipe(
        map((res) => {
          if (!res || typeof res !== 'object') {
            return {};
          }
          // Standard envelope: { data: { payments, ... } } — same as admin resource value.data
          if ('data' in res && res['data'] != null && typeof res['data'] === 'object') {
            return res['data'] as Record<string, unknown>;
          }
          return res as Record<string, unknown>;
        }),
      );
  }

  downloadBookingDocumentPdf(bookingId: string, documentType: string): Observable<Blob> {
    return this.http
      .getBlobResponse(
        `${API_CONFIG.bookings.downloadDocument(bookingId)}?documentType=${documentType}`,
      )
      .pipe(
        switchMap((response) =>
          mapPdfDownloadHttpResponse(response, 'Could not download document PDF'),
        ),
        catchError((err: unknown) =>
          mapPdfDownloadHttpError(err, 'Could not download document PDF'),
        ),
      );
  }

  uploadDocument(
    bookingId: string | null,
    payload: BookingDocumentUploadPayload,
  ): Observable<any> {
    if (!bookingId) return of(null);
    const body: BookingDocumentUploadPayload = {
      documentType: payload.documentType,
      fileUrl: payload.fileUrl,
    };
    if (payload.version !== undefined) {
      body.version = payload.version;
    }
    if (payload.partyType) {
      body.partyType = payload.partyType;
    }
    if (payload.customerId?.trim()) {
      body.customerId = payload.customerId.trim();
    }
    if (payload.originalFileName?.trim()) {
      body.originalFileName = payload.originalFileName.trim();
    }
    return this.http
      .post<ApiEnvelope<Record<string, unknown>>>(
        API_CONFIG.bookings.uploadDocument(bookingId),
        body,
      )
      .pipe(map((res) => unwrapApiData(res) ?? {}));
  }

  deleteDocument(bookingId: string, documentId: string): Observable<unknown> {
    return this.http.delete<ApiEnvelope<unknown>>(
      API_CONFIG.bookings.deleteDocument(bookingId, documentId),
    ).pipe(map((res) => unwrapApiData(res)));
  }

  addCoOwner(
    bookingId: string,
    body?: { customerId?: string },
  ): Observable<unknown> {
    return this.http
      .post<
        ApiEnvelope<unknown>
      >(API_CONFIG.bookings.addCoOwner(bookingId), body ?? {})
      .pipe(map((res) => unwrapApiData(res)));
  }

  getReceipt(
    _bookingId: string,
    receiptId: string,
  ): Observable<Record<string, unknown>> {
    return this.http
      .get<
        ApiEnvelope<Record<string, unknown>>
      >(API_CONFIG.bookings.getReceipt(receiptId))
      .pipe(map((res) => unwrapApiData(res) ?? {}));
  }

  downloadReceipt(_bookingId: string, receiptId: string): Observable<Blob> {
    return this.http
      .getBlob(API_CONFIG.bookings.downloadReceipt(receiptId))
      .pipe(map((blob) => toNativeBlob(blob)));
  }

  /** Creates or returns receipt metadata for an approved payment (admin parity). */
  generatePaymentReceipt(paymentId: string): Observable<Record<string, unknown>> {
    return this.http
      .post<ApiEnvelope<Record<string, unknown>>>(
        API_CONFIG.bookings.generateReceipt(paymentId),
        {},
      )
      .pipe(map((res) => unwrapApiData(res) ?? {}));
  }

  /** Creates or returns a booking receipt and resolves its id. */
  generatePaymentReceiptId(paymentId: string): Observable<string> {
    return this.generatePaymentReceipt(paymentId).pipe(
      map((body) => this.extractReceiptIdFromGenerateResponse(body)),
    );
  }

  /** Ensures a receipt exists for the payment, then downloads its PDF. */
  downloadReceiptPdfForPayment(paymentId: string): Observable<Blob> {
    return this.generatePaymentReceiptId(paymentId).pipe(
      switchMap((receiptId) => this.downloadReceipt('', receiptId)),
    );
  }

  private extractReceiptIdFromGenerateResponse(
    body: Record<string, unknown>,
  ): string {
    const rawId = body['id'] ?? body['receiptId'];
    const id = typeof rawId === 'string' ? rawId.trim() : '';
    if (!id) {
      throw new Error('Receipt id missing from generate response');
    }
    return id;
  }

  /** Record a manual booking payment (admin-parity payload). */
  submitManualPayment(payload: Record<string, unknown>): Observable<Record<string, unknown>> {
    return this.http
      .post<ApiEnvelope<Record<string, unknown>>>(
        API_CONFIG.bookings.recordPayment,
        payload,
      )
      .pipe(map((res) => unwrapApiData(res) ?? {}));
  }

  upsertOwnerKyc(
    bookingId: string,
    body: Record<string, unknown>,
  ): Observable<unknown> {
    return this.upsertOwnerWorkflowSection(
      bookingId,
      this.mapCustomerKycPayload(body),
    );
  }

  upsertOccupation(
    bookingId: string,
    body: Record<string, unknown>,
  ): Observable<unknown> {
    const payload: Record<string, unknown> = {
      partyType: body["partyType"] ?? "owner",
      occupation: body["occupation"] as Record<string, unknown>,
    };
    if (typeof body["customerId"] === "string" && body["customerId"].trim()) {
      payload["customerId"] = body["customerId"].trim();
    }
    return this.upsertOwnerWorkflowSection(bookingId, payload);
  }

  upsertFinancial(
    bookingId: string,
    body: Record<string, unknown>,
  ): Observable<unknown> {
    const payload: Record<string, unknown> = {
      partyType: body["partyType"] ?? "owner",
      financial: body["financial"] as Record<string, unknown>,
    };
    if (typeof body["customerId"] === "string" && body["customerId"].trim()) {
      payload["customerId"] = body["customerId"].trim();
    }
    return this.upsertOwnerWorkflowSection(bookingId, payload);
  }

  initiatePayment(
    bookingId: string,
    body: InitiateGatewayPaymentRequest,
  ): Observable<InitiateGatewayPaymentResponse> {
    return this.paymentGateway.initiate({
      ...body,
      bookingId,
    });
  }

  confirmPayment(
    body: ConfirmGatewayPaymentRequest,
  ): Observable<ConfirmGatewayPaymentResponse> {
    return this.paymentGateway.confirm(body);
  }

  mapBookingCards(
    rows: BookingListApiRow[] | undefined | null,
  ): BookingPropertyCard[] {
    if (!Array.isArray(rows)) {
      return [];
    }
    return rows.map((row) => {
      const imageRef = row.projectImageUrl?.trim() ?? "";
      return this.mapBookingCard(row, imageRef, imageRef);
    });
  }

  private resolveBookingCardImages(
    rows: BookingListApiRow[],
  ): Observable<BookingPropertyCard[]> {
    if (!rows.length) {
      return of([]);
    }

    return forkJoin(rows.map((row) => this.resolveBookingCardImage(row)));
  }

  private resolveBookingCardImage(
    row: BookingListApiRow,
  ): Observable<BookingPropertyCard> {
    const key = row.projectImageUrl?.trim() ?? "";
    if (!key) {
      return of(this.mapBookingCard(row, "", ""));
    }

    if (/^https?:\/\//i.test(key)) {
      return of(this.mapBookingCard(row, key, key));
    }

    return this.fileViewService.getViewableFileUrl(key).pipe(
      map((url) => this.mapBookingCard(row, key, url?.trim() ?? "")),
      catchError(() => of(this.mapBookingCard(row, key, ""))),
    );
  }

  mapBookingCard(
    row: BookingListApiRow,
    projectImageKey = "",
    resolvedImageUrl = "",
  ): BookingPropertyCard {
    const unit = row.unit ?? {};
    const developer = row.developerName?.trim();
    const location = row.location?.trim();
    let projectBy = "";
    if (developer) {
      projectBy = location ? `By ${developer},` : `By ${developer}`;
    }
    const area =
      unit.carpetAreaSqFt != null && Number.isFinite(unit.carpetAreaSqFt)
        ? `${Number(unit.carpetAreaSqFt).toFixed(2)} Sq.Ft`
        : "—";
    const unitType = unit.bhk?.trim() || "—";
    const propertyType =
      unitType !== "—" && area !== "—" ? `${unitType} (${area})` : unitType;

    return {
      id: row.id,
      workspaceId: row.workspaceId,
      workspaceName: row.workspaceName,
      name: row.projectName?.trim() || "—",
      badge: formatPortalStatus(row.status),
      status: row.status,
      projectBy,
      location: location || "",
      unitNo: unit.unitNumber?.trim() ? `Unit ${unit.unitNumber}` : "—",
      tower: unit.tower?.trim() || unit.wing?.trim() || "—",
      propertyType,
      parkingType: unit.parking?.trim() || "—",
      area,
      amount: formatInr(row.propertyAmount),
      bookingAmount: formatInr(row.bookingAmount),
      gstAmount: formatInr(row.gstAmount),
      registrationAmount:
        row.registrationFeeAmount != null
          ? formatInr(row.registrationFeeAmount)
          : "—",
      stampDuty:
        row.stampDutyAmount != null ? formatInr(row.stampDutyAmount) : "—",
      projectImageKey: projectImageKey,
      imageUrl: resolvedImageUrl,
    };
  }

  private extractBookingListRows(payload: unknown): unknown[] {
    if (Array.isArray(payload)) {
      return payload;
    }
    const record = rec(payload);
    const bookings = record["bookings"];
    if (Array.isArray(bookings)) {
      return bookings;
    }
    const data = record["data"];
    if (Array.isArray(data)) {
      return data;
    }
    if (data && typeof data === "object") {
      const nested = rec(data)["data"];
      if (Array.isArray(nested)) {
        return nested;
      }
    }
    return [];
  }

  private optionalApiString(
    record: ApiRecord,
    ...keys: string[]
  ): string | undefined {
    for (const key of keys) {
      const value = record[key];
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed) {
          return trimmed;
        }
      }
    }
    return undefined;
  }

  private mapListItemToApiRow(item: unknown): BookingListApiRow {
    const record = rec(item);
    if (record["unit"] && typeof record["unit"] === "object") {
      return this.mapBookingListApiRow(record);
    }
    return this.mapStandardListItemToApiRow(item as StandardBookingListItem);
  }

  private mapBookingListApiRow(record: ApiRecord): BookingListApiRow {
    const unit = rec(record["unit"]);
    const registration = rec(record["registration"]);
    const paymentSummary = rec(record["registrationPaymentSummary"]);

    return {
      id: String(record["id"] ?? ""),
      workspaceId: this.optionalApiString(record, "workspaceId", "workspace_id"),
      workspaceName: this.optionalApiString(record, "workspaceName", "workspace_name"),
      bookingId: String(record["bookingId"] ?? record["bookingNumber"] ?? ""),
      projectName: String(record["projectName"] ?? ""),
      developerName: (record["developerName"] as string | null) ?? null,
      location: (record["location"] as string | null) ?? null,
      projectImageUrl:
        (record["projectImageUrl"] as string | null) ??
        (record["coverImageUrl"] as string | null) ??
        (record["cover_image_url"] as string | null) ??
        null,
      status: String(record["status"] ?? ""),
      propertyAmount: this.pickListAmount(
        record["propertyAmount"],
        record["totalValue"],
      ),
      bookingAmount: this.pickListAmount(
        record["bookingAmount"],
        record["booking_amount"],
      ),
      gstAmount: this.pickListAmount(record["gstAmount"], record["gst_amount"]),
      unit: {
        tower: (unit["tower"] as string | null) ?? (record["towerName"] as string | null) ?? null,
        unitNumber:
          (unit["unitNumber"] as string | null) ??
          (record["unitNumber"] as string | null) ??
          null,
        wing: (unit["wing"] as string | null) ?? null,
        bhk:
          (unit["bhk"] as string | null) ??
          (record["unitType"] as string | null) ??
          null,
        carpetAreaSqFt: this.pickListAmount(
          unit["carpetAreaSqFt"],
          record["carpetAreaSqFt"],
        ),
        parking:
          (unit["parking"] as string | null) ??
          (record["carParking"] as string | null) ??
          null,
      },
      stampDutyAmount: this.pickListAmount(
        record["stampDutyAmount"],
        registration["stampDutyAmount"],
        paymentSummary["stampDutyAmount"],
        record["stamp_duty_amount"],
      ),
      registrationFeeAmount: this.pickListAmount(
        record["registrationFeeAmount"],
        registration["registrationFeeAmount"],
        paymentSummary["registrationFeeAmount"],
        record["registration_fee_amount"],
      ),
    };
  }

  private pickListAmount(...values: unknown[]): number | null {
    for (const value of values) {
      if (value == null || value === "") {
        continue;
      }
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
    return null;
  }

  private upsertOwnerWorkflowSection(
    bookingId: string,
    payload: Record<string, unknown>,
  ): Observable<unknown> {
    return this.http
      .post<
        ApiEnvelope<unknown>
      >(API_CONFIG.bookings.upsertOwnerWorkflow(bookingId), payload)
      .pipe(map((res) => unwrapApiData(res)));
  }

  buildOwnerWorkflowKycSection(
    body: Record<string, unknown>,
  ): Record<string, unknown> {
    return rec(this.mapCustomerKycPayload(body)["kyc"]);
  }

  private mapStandardListItemToApiRow(
    item: StandardBookingListItem,
  ): BookingListApiRow {
    const record = rec(item);
    const registration = rec(record["registration"]);
    const paymentSummary = rec(record["registrationPaymentSummary"]);

    return {
      id: item.id,
      bookingId: item.bookingNumber,
      projectName: item.projectName ?? "",
      developerName: item.developerName ?? null,
      location: item.projectAddress ?? item.location ?? null,
      projectImageUrl: item.projectImageUrl ?? null,
      status: item.status,
      propertyAmount: item.totalValue ?? null,
      bookingAmount: item.bookingAmount ?? null,
      gstAmount: item.gstAmount ?? null,
      unit: {
        tower: item.towerName ?? null,
        unitNumber: item.unitNumber ?? null,
        bhk: item.unitType ?? null,
        carpetAreaSqFt: item.carpetAreaSqFt ?? null,
        parking: item.carParking ?? null,
      },
      stampDutyAmount: this.pickListAmount(
        item.stampDutyAmount,
        record["stampDutyAmount"],
        registration["stampDutyAmount"],
        paymentSummary["stampDutyAmount"],
        record["stamp_duty_amount"],
      ),
      registrationFeeAmount: this.pickListAmount(
        item.registrationFeeAmount,
        record["registrationFeeAmount"],
        registration["registrationFeeAmount"],
        paymentSummary["registrationFeeAmount"],
        record["registration_fee_amount"],
      ),
    };
  }

  private mapBookingDetailToOverview(detail: ApiRecord): ApiRecord {
    const core = rec(detail["core"]);
    const project = rec(detail["project"]);
    const tower = rec(detail["tower"]);
    const unit = rec(detail["unit"]);
    const summary = rec(detail["summary"]);
    const sourceDetails = resolveEnquirySourceDetails(detail);
    const parkingNo = resolveParkingNo(detail);
    const parties =
      (detail["customerDetailParties"] as ApiRecord[] | undefined) ?? [];

    const propertyAddress = [project["city"], project["state"]]
      .filter(Boolean)
      .join(", ");

    const primaryCustomer = rec(detail["primaryCustomer"]);
   
    const prefixOptions = this.namePrefixOptionsService.getCachedOptions();
    const owners = mapOwnersFromBookingApi(detail, prefixOptions).map((owner) => ({
      partyType: owner.partyType,
      name: owner.name,
      mobileNo: owner.mobileNo,
      email: owner.email,
      panNumber: owner.panNumber,
      aadhaarNumber:
        extractAadhaarDisplayNumber(
          parties.find(
            (party) =>
              String(party["partyType"] ?? "owner").toLowerCase() === owner.partyType,
          ) ?? {},
          { summaryOwner: rec(summary["owner"]) },
        ) || null,
    }));

    if (owners.length === 0) {
      owners.push({
        partyType: "owner",
        name:
          pickContactString(
            primaryCustomer["displayName"],
            primaryCustomer["name"],
          ) || null,
        mobileNo: pickContactString(primaryCustomer["mobile"]) || null,
        email: pickContactString(primaryCustomer["email"]) || null,
        panNumber: null,
        aadhaarNumber: null,
      });
    }

    return {
      header: {
        bookingId: core["id"],
        bookingNumber: core["bookingNumber"] ?? summary["bookingNumber"],
        projectName: project["name"] ?? summary["projectName"],
        towerName: tower["towerName"] ?? tower["name"] ?? summary["towerName"],
        unitDetails: unit["unitType"] ?? summary["unitType"],
        propertyAddress: propertyAddress || null,
        status: core["status"] ?? summary["status"],
      },
      bookingDetails: {
        bookingId: core["id"],
        bookingStatus: core["status"],
        bookingDate: String(
          core["createdAt"] ?? summary["bookingDate"] ?? "",
        ).slice(0, 10),
        financeType: core["financeType"] ?? summary["financeType"],
      },
      owners,
      primaryCustomer: detail["primaryCustomer"] ?? null,
      propertyDetails: {
        projectName: project["name"] ?? summary["projectName"],
        towerName: tower["towerName"] ?? tower["name"] ?? summary["towerName"],
        unitNumber: unit["unitNumber"] ?? summary["unitNumber"],
        unitType: unit["unitType"] ?? summary["unitType"],
        floor: unit["floor"] ?? summary["floor"],
        carParking: parkingNo || unit["carParking"] || summary["carParking"],
        financeOption: core["financeType"] ?? summary["financeType"],
        propertyAmount: core["agreementValue"] ?? summary["totalValue"],
      },
      sourceDetails: {
        sourceType: sourceDetails.sourceType || null,
        name: sourceDetails.name || null,
        leadOwner: sourceDetails.leadOwner || null,
      },
    };
  }

  private mapBookingDetailToOwnerSummary(detail: ApiRecord): ApiRecord {
    const summaryOwner = rec(rec(detail["summary"])["owner"]);
    const primaryCustomer = rec(detail["primaryCustomer"]);
    const parties = resolvePartiesFromBookingApi(detail);
    const prefixOptions = this.namePrefixOptionsService.getCachedOptions();
    const owners = parties.map((party) => {
      const kyc = rec(party["kyc"]);
      const identity = rec(kyc["identity"]);
      const contact = rec(kyc["contact"]);
      const residential = rec(kyc["residential"]);
      const pan = rec(kyc["pan"]);
      const isOwner =
        String(party["partyType"]).trim().toLowerCase() === "owner";
      const displayName = resolvePartyPersonDisplayName(party, {
        isOwner,
        summaryOwner,
        primaryCustomer,
        prefixOptions,
      });
      return {
        fullName: displayName === "—" ? null : displayName,
        mobileNo:
          pickContactString(
            contact["mobile"],
            party["mobile"],
            party["mobileNo"],
            party["phoneNumber"],
            summaryOwner["mobile"],
            summaryOwner["mobileNo"],
            primaryCustomer["mobile"],
          ) || null,
        email:
          pickContactString(
            contact["email"],
            party["email"],
            party["emailId"],
            summaryOwner["email"],
            primaryCustomer["email"],
          ) || null,
        panNumber: pan["panNumber"] ?? summaryOwner["panMasked"] ?? null,
        aadhaarNumber: extractAadhaarDisplayNumber(party, { summaryOwner }) || null,
        nationality: identity["nationality"] ?? null,
        addressLine: residential["addressLine"] ?? null,
        city: residential["city"] ?? null,
        state: residential["state"] ?? null,
        country: residential["country"] ?? null,
        pinCode: residential["pinCode"] ?? null,
        partyType: party["partyType"] ?? "owner",
        customerId: party["customerId"] ?? primaryCustomer["id"] ?? null,
      };
    });

    if (owners.length === 0) {
      return {
        owners: [
          {
            fullName:
              pickContactString(
                primaryCustomer["displayName"],
                primaryCustomer["name"],
                summaryOwner["name"],
              ) || null,
            mobileNo:
              pickContactString(
                primaryCustomer["mobile"],
                summaryOwner["mobile"],
                summaryOwner["mobileNo"],
              ) || null,
            email:
              pickContactString(primaryCustomer["email"], summaryOwner["email"]) || null,
            panNumber: summaryOwner["panMasked"] ?? summaryOwner["panNumber"] ?? null,
            partyType: "owner",
            customerId: primaryCustomer["id"] ?? null,
          },
        ],
      };
    }

    return { owners };
  }

  private mapDashboardToPaymentSummary(
    dashboard: ApiRecord,
    detail: ApiRecord,
  ): ApiRecord {
    const paymentSummary = rec(dashboard["paymentSummary"]);
    const bookingSummary = rec(dashboard["bookingSummary"]);
    const paymentTotals = rec(detail["paymentTotals"]);
    const core = rec(detail["core"]);
    const snap = rec(core["pricingSnapshot"]);

    // Booking-token GST breakdown from paymentTotals (not property GST on pricingSnapshot).
    const bookingAmountBase = Number(
      paymentTotals["bookingAmountBase"] ??
        core["bookingAmount"] ??
        bookingSummary["bookingAmount"] ??
        0,
    );
    const bookingGstAmount = Number(paymentTotals["bookingGstAmount"] ?? 0);
    const bookingGstRate = Number(paymentTotals["bookingGstRate"] ?? 0);
    const bookingAmountRequired = Number(
      paymentTotals["bookingAmountRequired"] ??
        bookingAmountBase + bookingGstAmount,
    );
    const paidAmount = Number(
      paymentTotals["paidAmount"] ?? paymentSummary["paidAmount"] ?? 0,
    );
    const pendingAmount = Number(
      paymentTotals["pendingAmount"] ??
        paymentSummary["pendingAmount"] ??
        Math.max(0, bookingAmountRequired - paidAmount),
    );
    const unitAmount = Number(
      paymentTotals["agreementAmount"] ??
        paymentTotals["agreementValue"] ??
        bookingSummary["agreementAmount"] ??
        bookingSummary["agreementValue"] ??
        core["agreementAmount"] ??
        core["agreementValue"] ??
        paymentTotals["totalUnitPrice"] ??
        0,
    );

    const baseAmount = this.pickPricingNumber(snap, [
      "baseRate",
      "base_rate",
      "basicAmount",
      "basic_amount",
    ]);
    const floorRise = this.pickPricingNumber(snap, ["floorRise", "floor_rise"]);
    const otherCharges = this.pickPricingNumber(snap, [
      "otherCharges",
      "other_charges",
      "parking",
    ]);

    let status = "partially_paid";
    if (paidAmount <= 0) {
      status = "pending";
    } else if (pendingAmount <= 0) {
      status = "paid";
    }

    return {
      bookingId: bookingSummary["id"] ?? core["id"],
      bookingStatus: bookingSummary["status"] ?? core["status"],
      unitAmount,
      bookingAmount: bookingAmountBase,
      bookingAmountBase,
      bookingGstAmount,
      bookingGstRate,
      bookingAmountRequired,
      paidAmount,
      pendingAmount,
      status,
      payments: detail["payments"] ?? paymentTotals["payments"] ?? [],
      breakup: {
        baseAmount: baseAmount || unitAmount,
        floorRise,
        otherCharges,
        totalAmount:
          baseAmount + floorRise + otherCharges || bookingAmountRequired,
      },
    };
  }

  private mapCustomerKycPayload(
    body: Record<string, unknown>,
  ): Record<string, unknown> {
    const partyType = body["partyType"] ?? "owner";
    const entityType =
      body["ownerType"] === "non_individual" ? "non_individual" : "individual";
    const kycDetails = rec(body["kycDetails"]);
    const kyc: Record<string, unknown> = { entityType };

    this.applyPanAndAadhaarToKyc(kyc, body);
    this.applyKycIdentity(kyc, kycDetails);
    this.applyKycContact(kyc, kycDetails);
    this.applyKycResidential(kyc, kycDetails);

    const payload: Record<string, unknown> = { partyType, kyc };
    if (typeof body["customerId"] === "string" && body["customerId"].trim()) {
      payload["customerId"] = body["customerId"].trim();
    }
    return payload;
  }

  private applyPanAndAadhaarToKyc(
    kyc: Record<string, unknown>,
    body: Record<string, unknown>,
  ): void {
    if (body["alreadyPanVerified"] !== true && body["panNumber"]) {
      kyc["pan"] = {
        panNumber: body["panNumber"],
        verified: body["panVerified"] === true,
      };
    }
    if (body["alreadyAadhaarVerified"] !== true && body["aadhaarNumber"]) {
      kyc["aadhaar"] = {
        aadhaarNumber: body["aadhaarNumber"],
        verified: body["aadhaarVerified"] === true,
      };
    }
  }

  private applyKycIdentity(
    kyc: Record<string, unknown>,
    kycDetails: ApiRecord,
  ): void {
    const identity: Record<string, unknown> = {};
    if (kycDetails["fullName"]) identity["fullName"] = kycDetails["fullName"];
    const namePrefixId = kycDetails["namePrefixId"];
    if (namePrefixId === null || namePrefixId === '') {
      identity["namePrefixId"] = null;
    } else if (namePrefixId) {
      identity["namePrefixId"] = namePrefixId;
    }
    const genderValue = kycDetails["gender"];
    const apiGender = normalizeGenderForApi(
      typeof genderValue === "string" ? genderValue : undefined,
    );
    if (apiGender) identity["gender"] = apiGender;
    const dobValue = kycDetails["dateOfBirth"];
    const isoDob = formatToIsoDate(typeof dobValue === "string" ? dobValue : undefined);
    if (isoDob) identity["dateOfBirth"] = isoDob;
    if (Object.keys(identity).length) {
      kyc["identity"] = identity;
    }
  }

  private applyKycContact(
    kyc: Record<string, unknown>,
    kycDetails: ApiRecord,
  ): void {
    const contact: Record<string, unknown> = {};
    if (kycDetails["maritalStatus"]) contact["maritalStatus"] = kycDetails["maritalStatus"];
    if (kycDetails["email"]) contact["email"] = kycDetails["email"];
    if (kycDetails["mobile"]) contact["mobile"] = kycDetails["mobile"];
    if (Object.keys(contact).length) {
      kyc["contact"] = contact;
    }
  }

  private applyKycResidential(
    kyc: Record<string, unknown>,
    kycDetails: ApiRecord,
  ): void {
    const residential: Record<string, unknown> = {};
    if (kycDetails["addressLine"]) residential["addressLine"] = kycDetails["addressLine"];
    if (kycDetails["city"]) residential["city"] = kycDetails["city"];
    if (kycDetails["state"]) residential["state"] = kycDetails["state"];
    if (kycDetails["country"]) residential["country"] = kycDetails["country"];
    if (kycDetails["pinCode"]) residential["pinCode"] = kycDetails["pinCode"];
    if (Object.keys(residential).length) {
      kyc["residential"] = residential;
    }
  }

  private pickPricingNumber(snap: ApiRecord, keys: string[]): number {
    for (const key of keys) {
      const raw = snap[key];
      const num = Number(raw);
      if (Number.isFinite(num) && num > 0) {
        return num;
      }
    }
    return 0;
  }
}
